import type { FormEvent } from 'react'
import React, { useCallback, useEffect, useRef, useState } from 'react'

import ModalFooterActionContainer from '../TripModal/TripItems/Create/ModalFooterActionContainer'

import TextInput from 'components/Common/Form/Input/TextInput'
import useModalElementContext from 'hooks/Modal/useModalElementContext'
import { formToObject } from 'lib/forms/formToObject'
import TextButton from 'LuxKit/Button/TextButton'
import ModalBase from 'LuxKit/Modal/ModalBase'
import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalContent from 'LuxKit/Modal/ModalContent'
import ModalFooter from 'LuxKit/Modal/ModalFooter'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import { showSnackbar } from 'LuxKit/Snackbar/AppSnackbar'
import Caption from 'LuxKit/Typography/Caption'
import {
  useRenameAttachedDocument,
  useRenameUnattachedDocument,
} from 'tripPlanner/hooks/api/documents'
import useModalCallbacks from 'tripPlanner/hooks/View/useModalCallbacks'
import type { TripDocumentWithItemInfo } from 'tripPlanner/types/documents'

const MAX_FILENAME_CHARS = 100

interface Props {
  tripId: string
  document: TripDocumentWithItemInfo
}

function RenameDocumentModal({ tripId, document }: Props) {
  const inputRef = useRef<HTMLInputElement>(null)
  const { closeModal } = useModalCallbacks()
  const modalElement = useModalElementContext()
  const lastDotIndex = document.filename.lastIndexOf('.')
  const nameWithoutExt = lastDotIndex > 0 ?
      document.filename.substring(0, lastDotIndex) :
    document.filename
  const extension = lastDotIndex > 0 ?
      document.filename.substring(lastDotIndex) :
    ''
  const isAttached = !!document.item

  const [errorMsg, setErrorMsg] = useState('')
  const [nameLength, setNameLength] = useState(nameWithoutExt.length)

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus()
      inputRef.current.select()
    }
  }, [])

  const close = useCallback(() => {
    closeModal()
    modalElement?.resolve()
  }, [closeModal, modalElement])

  const { mutate: renameAttachedDocument, isLoading: isRenamingAttached } =
    useRenameAttachedDocument({
      onSuccess: () => {
        showSnackbar('Document renamed successfully', 'success')
        close()
      },
      onError: () => {
        showSnackbar('Failed to rename document', 'critical')
      },
      tripId,
    })

  const { mutate: renameUnattachedDocument, isLoading: isRenamingUnattached } =
    useRenameUnattachedDocument({
      onSuccess: () => {
        showSnackbar('Document renamed successfully', 'success')
        close()
      },
      onError: () => {
        showSnackbar('Failed to rename document', 'critical')
      },
      tripId,
    })

  const isLoading = isRenamingAttached || isRenamingUnattached

  const handleRename = useCallback(
    (e: FormEvent<HTMLFormElement>) => {
      e.preventDefault()

      const formData = formToObject<{ newName: string }>(e.currentTarget)
      const trimmedName = formData.newName.trim()

      if (!trimmedName) {
        setErrorMsg('Filename cannot be empty')
        return
      }

      if (trimmedName === nameWithoutExt) {
        close()
        return
      }

      const newFileName = trimmedName + extension

      if (isAttached && document.item) {
        renameAttachedDocument({
          tripId,
          tripItemId: document.item.id,
          fileName: document.filename,
          newFileName,
        })
      } else {
        renameUnattachedDocument({
          tripId,
          fileName: document.filename,
          newFileName,
        })
      }
    },
    [
      nameWithoutExt,
      extension,
      isAttached,
      document,
      tripId,
      close,
      renameAttachedDocument,
      renameUnattachedDocument,
    ],
  )

  const onInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setErrorMsg('')
    setNameLength(e.currentTarget.value.length)
  }, [])

  return (
    <ModalBase>
      <form onSubmit={handleRename}>
        <ModalHeader title="Rename your document" onCloseButtonClick={close} />
        <ModalBody>
          <ModalContent>
            <TextInput
              ref={inputRef}
              name="newName"
              label="Document name"
              placeholder="Enter new document name"
              defaultValue={nameWithoutExt}
              onChange={onInputChange}
              maxLength={MAX_FILENAME_CHARS}
              invalidErrorMessage={errorMsg}
              manualError={!!errorMsg}
              disabled={isLoading}
              required
              width="100%"
            />
            <Caption variant="medium" colour="neutral-three" align="end">
              {nameLength}/{MAX_FILENAME_CHARS}
            </Caption>
          </ModalContent>
        </ModalBody>
        <ModalFooter>
          <ModalFooterActionContainer>
            <TextButton
              kind="tertiary"
              size="large"
              fit="mobile-full-width"
              onClick={close}
              disabled={isLoading}
            >
              Cancel
            </TextButton>
            <TextButton
              kind="primary"
              size="large"
              fit="mobile-full-width"
              type="submit"
              disabled={isLoading}
            >
              Confirm
            </TextButton>
          </ModalFooterActionContainer>
        </ModalFooter>
      </form>
    </ModalBase>
  )
}

export default RenameDocumentModal
